// src/admin/TranscriptHistoryManager.js - Quản lý Lịch Sử Sửa Điểm & Hoàn Tác Khôi Phục
import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useOutletContext } from 'react-router-dom';
import { StudentContext } from '../StudentContext';
import { firestore } from '../firebaseConfig';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import {
  subscribeTranscriptHistory,
  rollbackTranscriptHistoryLog,
  TRANSCRIPT_FIELD_LABELS
} from '../FirebaseService';
import * as XLSX from 'xlsx';
import './TranscriptHistoryManager.css';

const formatLogTime = (ts) => {
  if (!ts) return '-';
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString('vi-VN', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch (e) {
    return String(ts);
  }
};

const getRoleBadge = (role, sourceScreen) => {
  if (sourceScreen === 'TranscriptHistoryRollback') {
    return { label: '↩️ Khôi phục điểm', cls: 'rollback' };
  }
  if (role === 'admin') return { label: '🛡️ Admin', cls: 'admin' };
  if (role === 'substitute') return { label: '🤝 GLV Phụ lớp', cls: 'substitute' };
  return { label: '👨‍🏫 GLV Đứng lớp', cls: 'catechist' };
};

const TranscriptHistoryManager = () => {
  const { currentYearId: contextYearId, classMap } = useContext(StudentContext);
  const outletContext = useOutletContext() || {};
  const sidebarSelectedClass = outletContext.selectedClassForOutlet || 'all';

  const [years, setYears] = useState([]);
  const [selectedYearId, setSelectedYearId] = useState('');
  const [historyLogs, setHistoryLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Bộ lọc
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('ALL'); // 'ALL' | 'CLEARED' | 'CATECHIST' | 'ADMIN' | 'REVERTED'
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
  const [showAutoFields, setShowAutoFields] = useState(false);

  // Mở rộng chi tiết từng logId
  const [expandedLogIds, setExpandedLogIds] = useState({});
  const [rollingBackKey, setRollingBackKey] = useState(null);

  // 1. Tải danh sách Niên khóa
  useEffect(() => {
    const fetchYears = async () => {
      try {
        const q = query(collection(firestore, 'academic_years'), orderBy('startDate', 'desc'));
        const snap = await getDocs(q);
        const yList = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setYears(yList);

        if (yList.length > 0) {
          const defaultYear = yList.find((y) => y.isCurrent)?.id || contextYearId || yList[0].id;
          setSelectedYearId(defaultYear);
        }
      } catch (err) {
        console.error('Lỗi tải danh sách niên khóa:', err);
      }
    };
    fetchYears();
  }, [contextYearId]);

  // 2. Lắng nghe Realtime Nhật ký sửa điểm
  useEffect(() => {
    if (!selectedYearId) return;
    setLoading(true);

    const unsubscribe = subscribeTranscriptHistory(selectedYearId, (logs) => {
      setHistoryLogs(logs);
      setLoading(false);
    });

    return () => unsubscribe && unsubscribe();
  }, [selectedYearId]);

  const toggleExpand = (logId) => {
    setExpandedLogIds((prev) => ({ ...prev, [logId]: !prev[logId] }));
  };

  // 3. Lọc danh sách nhật ký
  const filteredLogs = useMemo(() => {
    return historyLogs.filter((log) => {
      // (A) Lọc theo Lớp trên Sidebar
      if (sidebarSelectedClass && sidebarSelectedClass !== 'all') {
        const targetClassName = classMap[sidebarSelectedClass] || sidebarSelectedClass;
        const matchClassDirect = log.classId === sidebarSelectedClass || log.className === targetClassName;
        const matchClassInChanges = (log.changes || []).some(
          (ch) => ch.classId === sidebarSelectedClass || ch.className === targetClassName
        );
        if (!matchClassDirect && !matchClassInChanges) return false;
      }

      // (B) Lọc theo Phân loại / Cảnh báo
      if (typeFilter === 'CLEARED' && !(log.clearedCellsCount > 0)) return false;
      if (typeFilter === 'CATECHIST' && log.actorRole === 'admin') return false;
      if (typeFilter === 'ADMIN' && log.actorRole !== 'admin') return false;
      if (typeFilter === 'REVERTED' && !log.isReverted && !log.partiallyReverted) return false;

      // (C) Lọc theo Khoảng ngày
      if (dateRange.startDate || dateRange.endDate) {
        const logDate = log.createdAt?.toDate ? log.createdAt.toDate() : new Date(log.createdAt);
        if (dateRange.startDate) {
          const s = new Date(dateRange.startDate);
          s.setHours(0, 0, 0, 0);
          if (logDate < s) return false;
        }
        if (dateRange.endDate) {
          const e = new Date(dateRange.endDate);
          e.setHours(23, 59, 59, 999);
          if (logDate > e) return false;
        }
      }

      // (D) Lọc theo Từ khóa tìm kiếm (Tên GLV, Lớp, Mã TN, Tên thiếu nhi)
      if (searchTerm.trim()) {
        const q = searchTerm.toLowerCase().trim();
        const matchHeader =
          (log.actorName || '').toLowerCase().includes(q) ||
          (log.className || '').toLowerCase().includes(q) ||
          (log.note || '').toLowerCase().includes(q);

        const matchStudent = (log.changes || []).some(
          (ch) =>
            (ch.studentId || '').toLowerCase().includes(q) ||
            (ch.fullName || '').toLowerCase().includes(q) ||
            (ch.holyName || '').toLowerCase().includes(q)
        );

        if (!matchHeader && !matchStudent) return false;
      }

      return true;
    });
  }, [historyLogs, sidebarSelectedClass, classMap, typeFilter, dateRange, searchTerm]);

  // Thống kê nhanh
  const stats = useMemo(() => {
    const total = filteredLogs.length;
    const hasCleared = filteredLogs.filter((l) => (l.clearedCellsCount || 0) > 0 && !l.isReverted).length;
    const reverted = filteredLogs.filter((l) => l.isReverted || l.partiallyReverted).length;
    return { total, hasCleared, reverted };
  }, [filteredLogs]);

  // 4. Hoàn tác cả lần lưu hoặc riêng 1 thiếu nhi
  const handleRollback = async (logDoc, targetStudentId = null, studentName = '') => {
    const confirmMsg = targetStudentId
      ? `XÁC NHẬN KHÔI PHỤC ĐIỂM CÁ NHÂN:\n\n• Thiếu nhi: ${studentName} (${targetStudentId})\n• Đưa toàn bộ các ô điểm bị sửa trong lượt này quay về giá trị cũ trước khi lưu?`
      : `XÁC NHẬN HOÀN TÁC TOÀN BỘ LƯỢT LƯU:\n\n• Người lưu: ${logDoc.actorName}\n• Lớp: ${logDoc.className}\n• Số lượng: ${logDoc.totalStudentsChanged} thiếu nhi\n\nToàn bộ điểm trong lần lưu này sẽ được khôi phục về giá trị trước đó. Tiếp tục?`;

    if (!window.confirm(confirmMsg)) return;

    const actionKey = targetStudentId ? `${logDoc.id}-${targetStudentId}` : logDoc.id;
    setRollingBackKey(actionKey);

    try {
      const adminName = `${localStorage.getItem('catechistHolyName') || ''} ${
        localStorage.getItem('catechistFullName') || 'Admin'
      }`.trim();

      const res = await rollbackTranscriptHistoryLog({
        yearId: selectedYearId,
        logDoc,
        targetStudentId,
        adminName,
        adminUid: localStorage.getItem('uid') || ''
      });

      if (res.success) {
        alert(
          targetStudentId
            ? `🎉 Đã khôi phục điểm cũ thành công cho thiếu nhi ${studentName}!`
            : `🎉 Đã hoàn tác thành công điểm cho ${res.restoredStudentsCount} thiếu nhi!`
        );
      } else {
        alert('❌ ' + (res.message || 'Không thể hoàn tác.'));
      }
    } catch (err) {
      alert('❌ Lỗi hệ thống: ' + err.message);
    } finally {
      setRollingBackKey(null);
    }
  };

  // 5. Xuất Excel Nhật ký chi tiết
  const handleExportExcel = () => {
    if (filteredLogs.length === 0) {
      alert('Không có dữ liệu lịch sử để xuất Excel.');
      return;
    }

    const rows = [
      [
        'Thời Gian Lưu',
        'Người Thực Hiện',
        'Vai Trò',
        'Màn Hình',
        'Lớp',
        'Mã TN',
        'Tên Thánh & Họ Tên',
        'Cột Điểm',
        'Điểm Cũ',
        'Điểm Mới',
        'Trạng Thái Hoàn Tác'
      ]
    ];

    filteredLogs.forEach((log) => {
      const timeStr = formatLogTime(log.createdAt);
      const roleObj = getRoleBadge(log.actorRole, log.sourceScreen);

      (log.changes || []).forEach((ch) => {
        if (!showAutoFields && ch.isAutoCalculated) return;
        rows.push([
          timeStr,
          log.actorName || '',
          roleObj.label,
          log.sourceScreen === 'AdminTranscriptManager' ? 'Toàn trường (Admin)' : 'Bảng điểm lớp',
          ch.className || log.className || '',
          ch.studentId,
          `${ch.holyName ? ch.holyName + ' ' : ''}${ch.fullName}`,
          TRANSCRIPT_FIELD_LABELS[ch.fieldPath] || ch.fieldPath,
          ch.oldVal === '' ? '(Trống)' : ch.oldVal,
          ch.newVal === '' ? '(Trống)' : ch.newVal,
          ch.reverted ? `Đã khôi phục (${ch.revertedBy || ''})` : 'Đang áp dụng'
        ]);
      });
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, 'LichSuSuaDiem');
    XLSX.writeFile(wb, `LichSuSuaDiem_${selectedYearId}.xlsx`);
  };

  return (
    <div className="thm-container">
      {/* HEADER */}
      <div className="thm-header">
        <div>
          <h2 className="thm-title">📜 Nhật Ký Lưu / Sửa Bảng Điểm & Khôi Phục</h2>
          <p className="thm-subtitle">
            Theo dõi chi tiết mọi thay đổi điểm số của Giáo lý viên và Admin. Hỗ trợ phát hiện xóa nhầm điểm và khôi phục lại điểm cũ chỉ với 1 cú nhấp.
          </p>
        </div>
        <button onClick={handleExportExcel} className="thm-btn thm-btn-excel">
          📥 Xuất Excel Nhật Ký
        </button>
      </div>

      {/* THỐNG KÊ NHANH */}
      <div className="thm-stats-grid">
        <div className="thm-stat-card blue">
          <span className="thm-stat-num">{stats.total}</span>
          <span className="thm-stat-lbl">Tổng Lượt Lưu Điểm</span>
        </div>
        <div className="thm-stat-card red">
          <span className="thm-stat-num">{stats.hasCleared}</span>
          <span className="thm-stat-lbl">Lượt Có Xóa Trắng Điểm Cũ</span>
        </div>
        <div className="thm-stat-card orange">
          <span className="thm-stat-num">{stats.reverted}</span>
          <span className="thm-stat-lbl">Lượt Đã Hoàn Tác / Khôi Phục</span>
        </div>
      </div>

      {/* THANH CÔNG CỤ LỌC */}
      <div className="thm-toolbar-card">
        <div className="thm-filter-row">
          <div className="thm-filter-item">
            <label className="thm-label">📅 Niên khóa:</label>
            <select
              value={selectedYearId}
              onChange={(e) => setSelectedYearId(e.target.value)}
              className="thm-select"
            >
              {years.map((y) => (
                <option key={y.id} value={y.id}>
                  {y.name} {y.isCurrent ? '⭐ [Hiện tại]' : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="thm-filter-item">
            <label className="thm-label">🔍 Bộ lọc:</label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="thm-select"
            >
              <option value="ALL">-- Tất cả lượt lưu --</option>
              <option value="CLEARED">⚠️ Có xóa trắng điểm cũ</option>
              <option value="CATECHIST">👨‍🏫 Lưu bởi Giáo lý viên</option>
              <option value="ADMIN">🛡️ Lưu bởi Admin</option>
              <option value="REVERTED">↩️ Đã hoàn tác / khôi phục</option>
            </select>
          </div>

          <div className="thm-filter-item">
            <label className="thm-label">Từ:</label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
              className="thm-date-input"
            />
            <label className="thm-label">Đến:</label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
              className="thm-date-input"
            />
          </div>
        </div>

        <div className="thm-filter-row" style={{ marginTop: '10px' }}>
          <input
            type="text"
            placeholder="🔎 Tìm tên thiếu nhi, mã TN, tên Giáo lý viên sửa điểm, tên lớp..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="thm-search-input"
          />

          <label className="thm-checkbox-label">
            <input
              type="checkbox"
              checked={showAutoFields}
              onChange={(e) => setShowAutoFields(e.target.checked)}
            />
            Hiện cả ô TB / Xếp hạng tự động
          </label>
        </div>
      </div>

      {/* DANH SÁCH CÁC LẦN LƯU ĐIỂM */}
      {loading ? (
        <div className="thm-empty-box">⏳ Đang tải nhật ký sửa điểm...</div>
      ) : filteredLogs.length === 0 ? (
        <div className="thm-empty-box">
          Chưa có nhật ký lưu/sửa điểm nào phù hợp với bộ lọc hiện tại.
        </div>
      ) : (
        <div className="thm-logs-list">
          {filteredLogs.map((log) => {
            const isExpanded = Boolean(expandedLogIds[log.id]);
            const roleBadge = getRoleBadge(log.actorRole, log.sourceScreen);
            const isBatchRollingBack = rollingBackKey === log.id;

            // Gom các thay đổi theo từng thiếu nhi để hiển thị gọn đẹp khi mở rộng
            const groupedByStudent = {};
            (log.changes || []).forEach((ch) => {
              if (!showAutoFields && ch.isAutoCalculated) return;
              if (!groupedByStudent[ch.studentId]) {
                groupedByStudent[ch.studentId] = {
                  studentId: ch.studentId,
                  holyName: ch.holyName,
                  fullName: ch.fullName,
                  className: ch.className || log.className,
                  items: []
                };
              }
              groupedByStudent[ch.studentId].items.push(ch);
            });

            const studentGroups = Object.values(groupedByStudent);

            return (
              <div
                key={log.id}
                className={`thm-log-card ${log.clearedCellsCount > 0 && !log.isReverted ? 'has-warning' : ''} ${
                  log.isReverted ? 'is-reverted' : ''
                }`}
              >
                {/* DÒNG TÓM TẮT CỦA LẦN LƯU */}
                <div className="thm-log-summary" onClick={() => toggleExpand(log.id)}>
                  <div className="thm-log-left">
                    <span className="thm-time-badge">🕒 {formatLogTime(log.createdAt)}</span>
                    <span className="thm-class-badge">🏫 {log.className || 'Nhiều lớp'}</span>
                    <span className={`thm-role-badge ${roleBadge.cls}`}>{roleBadge.label}</span>
                    <strong className="thm-actor-name">{log.actorName}</strong>
                  </div>

                  <div className="thm-log-right" onClick={(e) => e.stopPropagation()}>
                    <span className="thm-scope-pill">
                      <strong>{log.totalStudentsChanged}</strong> thiếu nhi •{' '}
                      <strong>{log.manualCellsChanged ?? log.totalCellsChanged}</strong> ô sửa
                    </span>

                    {log.clearedCellsCount > 0 && (
                      <span className="thm-cleared-badge" title="Lượt lưu này có xóa trắng điểm đã nhập từ trước">
                        ⚠️ Xóa {log.clearedCellsCount} ô
                      </span>
                    )}

                    {log.isReverted ? (
                      <span className="thm-status-pill reverted">↩️ Đã hoàn tác</span>
                    ) : log.partiallyReverted ? (
                      <span className="thm-status-pill partial">⚡ Khôi phục 1 phần</span>
                    ) : (
                      <span className="thm-status-pill active">✓ Hiệu lực</span>
                    )}

                    <button
                      type="button"
                      onClick={() => toggleExpand(log.id)}
                      className="thm-btn-detail"
                    >
                      {isExpanded ? '▲ Thu gọn' : '👁️ Chi tiết'}
                    </button>

                    {log.sourceScreen !== 'TranscriptHistoryRollback' && (
                      <button
                        type="button"
                        disabled={log.isReverted || isBatchRollingBack}
                        onClick={() => handleRollback(log, null)}
                        className="thm-btn-revert-all"
                        title="Đưa toàn bộ điểm trong lần lưu này về giá trị cũ"
                      >
                        {isBatchRollingBack ? '⏳...' : '↩️ Hoàn tác'}
                      </button>
                    )}
                  </div>
                </div>

                {/* VÙNG CHI TIẾT TỪNG THIẾU NHI KHI BẤM MỞ RỘNG */}
                {isExpanded && (
                  <div className="thm-log-details">
                    {log.note && <div className="thm-log-note">📌 Ghi chú: {log.note}</div>}
                    {log.revertedBy && (
                      <div className="thm-reverted-banner">
                        ↩️ Đã thực hiện khôi phục bởi <strong>{log.revertedBy}</strong> lúc{' '}
                        {formatLogTime(log.revertedAt)}
                      </div>
                    )}

                    {studentGroups.length === 0 ? (
                      <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>
                        Chỉ có các cột điểm TB / Xếp hạng tự động thay đổi (Bật "Hiện cả ô TB / Xếp hạng tự động" ở trên để xem).
                      </p>
                    ) : (
                      <table className="thm-detail-table">
                        <thead>
                          <tr>
                            <th style={{ width: '90px' }}>Mã TN</th>
                            <th style={{ width: '210px' }}>Thiếu Nhi</th>
                            <th style={{ width: '130px' }}>Lớp</th>
                            <th>Chi Tiết Thay Đổi (Cũ ➔ Mới)</th>
                            <th style={{ width: '140px', textAlign: 'center' }}>Khôi Phục Lẻ</th>
                          </tr>
                        </thead>
                        <tbody>
                          {studentGroups.map((stGroup) => {
                            const allStReverted = stGroup.items.every((i) => i.reverted);
                            const stActionKey = `${log.id}-${stGroup.studentId}`;
                            const isStRolling = rollingBackKey === stActionKey;

                            return (
                              <tr key={stGroup.studentId}>
                                <td className="thm-code">{stGroup.studentId}</td>
                                <td>
                                  <span className="thm-holy">{stGroup.holyName}</span>
                                  <strong>{stGroup.fullName}</strong>
                                </td>
                                <td>{stGroup.className}</td>
                                <td>
                                  <div className="thm-diff-chips">
                                    {stGroup.items.map((ch, idx) => (
                                      <span
                                        key={`${ch.fieldPath}-${idx}`}
                                        className={`thm-diff-chip ${ch.isCleared ? 'cleared' : ''} ${
                                          ch.reverted ? 'reverted' : ''
                                        }`}
                                      >
                                        <strong>{TRANSCRIPT_FIELD_LABELS[ch.fieldPath] || ch.fieldPath}:</strong>{' '}
                                        <span className="old-v">{ch.oldVal === '' ? 'Trống' : ch.oldVal}</span>
                                        {' ➔ '}
                                        <span className="new-v">{ch.newVal === '' ? 'Trống (Xóa)' : ch.newVal}</span>
                                      </span>
                                    ))}
                                  </div>
                                </td>
                                <td style={{ textAlign: 'center' }}>
                                  {log.sourceScreen !== 'TranscriptHistoryRollback' && (
                                    <button
                                      type="button"
                                      disabled={allStReverted || isStRolling}
                                      onClick={() =>
                                        handleRollback(
                                          log,
                                          stGroup.studentId,
                                          `${stGroup.holyName ? stGroup.holyName + ' ' : ''}${stGroup.fullName}`
                                        )
                                      }
                                      className="thm-btn-revert-student"
                                    >
                                      {isStRolling
                                        ? '⏳...'
                                        : allStReverted
                                        ? '✓ Đã khôi phục'
                                        : '↩️ Khôi phục em này'}
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TranscriptHistoryManager;